import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Create context
const MediaCacheContext = createContext();

// Custom hook to use the cache
export const useMediaCache = () => {
  const context = useContext(MediaCacheContext);
  if (!context) {
    throw new Error('useMediaCache must be used within MediaCacheProvider');
  }
  return context;
};

// Debug flag via URL: ?mm_debug=1
const isDebugEnabled = (() => {
  try {
    if (typeof window === 'undefined') return false;
    const url = new URL(window.location.href);
    return url.searchParams.get('mm_debug') === '1';
  } catch (e) {
    return false;
  }
})();

// Cache Provider Component
export const MediaCacheProvider = ({ children }) => {
  if (isDebugEnabled) console.log('[MediaCache] 🔵 Provider MOUNTING');
  
  // Cache structure
  const [cache, setCache] = useState({
    pictures: {
      data: [],
      timestamp: null,
      isValid: false
    },
    videos: {
      data: [],
      timestamp: null,
      isValid: false
    },
    albums: {
      // 'album-id': { data: {...}, timestamp: ..., isValid: true }
    },
    archivedAssets: {
      // 'album-id': { data: [...], timestamp: ..., isValid: true }
    },
    lastVideoCreationTimes: {
      // 'album-id': timestamp
    }
  });
  
  if (isDebugEnabled) console.log('[MediaCache] ✅ cache state initialized');

  // Performance metrics
  const metricsRef = useRef({
    cacheHits: 0,
    cacheMisses: 0,
    apiCalls: 0
  });

  // Get cached pictures
  const getCachedPictures = useCallback(() => {
    if (cache.pictures.isValid) {
      // Verbose log removed for production
      metricsRef.current.cacheHits++;
      return cache.pictures.data;
    }
    console.log('❌ Cache MISS: Pictures');
    metricsRef.current.cacheMisses++;
    return null;
  }, [cache.pictures]);

  // Set cached pictures
  const setCachedPictures = useCallback((pictures) => {
    // Verbose log removed for production
    setCache(prev => ({
      ...prev,
      pictures: {
        data: pictures,
        timestamp: Date.now(),
        isValid: true
      }
    }));
  }, []);

  // Get cached videos
  const getCachedVideos = useCallback(() => {
    if (cache.videos.isValid) {
      // Verbose log removed for production
      metricsRef.current.cacheHits++;
      return cache.videos.data;
    }
    console.log('❌ Cache MISS: Videos');
    metricsRef.current.cacheMisses++;
    return null;
  }, [cache.videos]);

  // Set cached videos
  const setCachedVideos = useCallback((videos) => {
    // Verbose log removed for production
    setCache(prev => ({
      ...prev,
      videos: {
        data: videos,
        timestamp: Date.now(),
        isValid: true
      }
    }));
  }, []);

  // Get cached album
  const getCachedAlbum = useCallback((albumId) => {
    const albumCache = cache.albums[albumId];
    if (albumCache && albumCache.isValid) {
      // Verbose log removed for production
      metricsRef.current.cacheHits++;
      return albumCache.data;
    }
    console.log(`❌ Cache MISS: Album ${albumId}`);
    metricsRef.current.cacheMisses++;
    return null;
  }, [cache.albums]);

  // Set cached album
  const setCachedAlbum = useCallback((albumId, albumData) => {
    // Verbose log removed for production
    setCache(prev => ({
      ...prev,
      albums: {
        ...prev.albums,
        [albumId]: {
          data: albumData,
          timestamp: Date.now(),
          isValid: true
        }
      }
    }));
  }, []);

  // Get cached archived assets for an album
  const getCachedArchivedAssets = useCallback((albumId) => {
    const archivedCache = cache.archivedAssets[albumId];
    if (archivedCache && archivedCache.isValid) {
      // Verbose log removed for production
      metricsRef.current.cacheHits++;
      return archivedCache.data;
    }
    console.log(`❌ Cache MISS: Archived assets for album ${albumId}`);
    metricsRef.current.cacheMisses++;
    return null;
  }, [cache.archivedAssets]);

  // Set cached archived assets
  const setCachedArchivedAssets = useCallback((albumId, archivedAssets) => {
    // Verbose log removed for production
    setCache(prev => ({
      ...prev,
      archivedAssets: {
        ...prev.archivedAssets,
        [albumId]: {
          data: archivedAssets,
          timestamp: Date.now(),
          isValid: true
        }
      }
    }));
  }, []);

  // INVALIDATION: Update a single picture across all caches
  const updatePictureInCache = useCallback((pictureId, updatedFields) => {
    console.log(`🔄 Updating picture ${pictureId} in all caches`);
    
    setCache(prev => {
      const newCache = { ...prev };
      
      // Update in pictures cache
      if (newCache.pictures.isValid) {
        newCache.pictures.data = newCache.pictures.data.map(pic =>
          pic.id === pictureId ? { ...pic, ...updatedFields } : pic
        );
      }
      
      // Update in all album caches
      Object.keys(newCache.albums).forEach(albumId => {
        if (newCache.albums[albumId].isValid && newCache.albums[albumId].data.assets) {
          newCache.albums[albumId].data.assets = newCache.albums[albumId].data.assets.map(asset =>
            asset.id === pictureId ? { ...asset, ...updatedFields } : asset
          );
        }
      });
      
      return newCache;
    });
  }, []);

  // INVALIDATION: Update a single video across all caches
  const updateVideoInCache = useCallback((videoId, updatedFields) => {
    console.log(`🔄 Updating video ${videoId} in all caches`);
    
    setCache(prev => {
      const newCache = { ...prev };
      
      // Update in videos cache
      if (newCache.videos.isValid) {
        newCache.videos.data = newCache.videos.data.map(vid =>
          vid.id === videoId ? { ...vid, ...updatedFields } : vid
        );
      }
      
      // Update in all album caches
      Object.keys(newCache.albums).forEach(albumId => {
        if (newCache.albums[albumId].isValid && newCache.albums[albumId].data.assets) {
          newCache.albums[albumId].data.assets = newCache.albums[albumId].data.assets.map(asset =>
            asset.id === videoId ? { ...asset, ...updatedFields } : asset
          );
        }
      });
      
      return newCache;
    });
  }, []);

  // INVALIDATION: Add new asset to caches
  const addAssetToCache = useCallback((asset) => {
    console.log(`➕ Adding ${asset.asset_type} ${asset.id} to caches`);
    
    setCache(prev => {
      const newCache = { ...prev };
      
      // Add to pictures/videos cache
      if (asset.asset_type === 'image' && newCache.pictures.isValid) {
        newCache.pictures.data = [asset, ...newCache.pictures.data];
      } else if (asset.asset_type === 'video' && newCache.videos.isValid) {
        newCache.videos.data = [asset, ...newCache.videos.data];
      }
      
      // Add to album cache if album_id present
      if (asset.album_id && newCache.albums[asset.album_id]?.isValid) {
        newCache.albums[asset.album_id].data.assets = [
          asset,
          ...newCache.albums[asset.album_id].data.assets
        ];
      }
      
      return newCache;
    });
  }, []);

  // INVALIDATION: Remove asset from caches (for delete)
  const removeAssetFromCache = useCallback((assetId) => {
    console.log(`🗑️ Removing asset ${assetId} from all caches`);
    
    setCache(prev => {
      const newCache = { ...prev };
      
      // Remove from pictures cache
      if (newCache.pictures.isValid) {
        newCache.pictures.data = newCache.pictures.data.filter(pic => pic.id !== assetId);
      }
      
      // Remove from videos cache
      if (newCache.videos.isValid) {
        newCache.videos.data = newCache.videos.data.filter(vid => vid.id !== assetId);
      }
      
      // Remove from all album caches
      Object.keys(newCache.albums).forEach(albumId => {
        if (newCache.albums[albumId].isValid && newCache.albums[albumId].data.assets) {
          newCache.albums[albumId].data.assets = newCache.albums[albumId].data.assets.filter(
            asset => asset.id !== assetId
          );
        }
      });
      
      // Remove from archived caches
      Object.keys(newCache.archivedAssets).forEach(albumId => {
        if (newCache.archivedAssets[albumId].isValid) {
          newCache.archivedAssets[albumId].data = newCache.archivedAssets[albumId].data.filter(
            asset => asset.id !== assetId
          );
        }
      });
      
      return newCache;
    });
  }, []);

  // INVALIDATION: Move asset to archive
  const archiveAssetInCache = useCallback((assetId, albumId) => {
    console.log(`📦 Moving asset ${assetId} to archive in cache`);
    
    setCache(prev => {
      const newCache = { ...prev };
      
      // Find the asset in album cache
      let archivedAsset = null;
      if (newCache.albums[albumId]?.isValid) {
        archivedAsset = newCache.albums[albumId].data.assets.find(a => a.id === assetId);
        
        // Remove from album cache
        newCache.albums[albumId].data.assets = newCache.albums[albumId].data.assets.filter(
          a => a.id !== assetId
        );
      }
      
      // Add to archived cache if we found the asset
      if (archivedAsset) {
        archivedAsset.archived = true;
        if (!newCache.archivedAssets[albumId]) {
          newCache.archivedAssets[albumId] = {
            data: [],
            timestamp: Date.now(),
            isValid: true
          };
        }
        newCache.archivedAssets[albumId].data = [
          archivedAsset,
          ...newCache.archivedAssets[albumId].data
        ];
      }
      
      return newCache;
    });
  }, []);

  // INVALIDATION: Restore asset from archive
  const restoreAssetInCache = useCallback((assetId, albumId) => {
    console.log(`♻️ Restoring asset ${assetId} from archive in cache`);
    
    setCache(prev => {
      const newCache = { ...prev };
      
      // Find the asset in archived cache
      let restoredAsset = null;
      if (newCache.archivedAssets[albumId]?.isValid) {
        restoredAsset = newCache.archivedAssets[albumId].data.find(a => a.id === assetId);
        
        // Remove from archived cache
        newCache.archivedAssets[albumId].data = newCache.archivedAssets[albumId].data.filter(
          a => a.id !== assetId
        );
      }
      
      // Add back to album cache
      if (restoredAsset && newCache.albums[albumId]?.isValid) {
        restoredAsset.archived = false;
        newCache.albums[albumId].data.assets = [
          restoredAsset,
          ...newCache.albums[albumId].data.assets
        ];
      }
      
      return newCache;
    });
  }, []);

  // INVALIDATION: Clear specific cache
  const invalidateCache = useCallback((type, id = null) => {
    console.log(`🔄 Invalidating cache: ${type}${id ? ` (${id})` : ''}`);
    
    setCache(prev => {
      const newCache = { ...prev };
      
      switch (type) {
        case 'pictures':
          newCache.pictures.isValid = false;
          break;
        case 'videos':
          newCache.videos.isValid = false;
          break;
        case 'album':
          if (id && newCache.albums[id]) {
            newCache.albums[id].isValid = false;
          }
          break;
        case 'archived':
          if (id && newCache.archivedAssets[id]) {
            newCache.archivedAssets[id].isValid = false;
          }
          break;
        case 'all':
          // Invalidate everything
          newCache.pictures.isValid = false;
          newCache.videos.isValid = false;
          Object.keys(newCache.albums).forEach(albumId => {
            newCache.albums[albumId].isValid = false;
          });
          Object.keys(newCache.archivedAssets).forEach(albumId => {
            newCache.archivedAssets[albumId].isValid = false;
          });
          break;
        default:
          console.warn(`Unknown cache type: ${type}`);
      }
      
      return newCache;
    });
  }, []);

  // Get cache metrics
  const getCacheMetrics = useCallback(() => {
    const metrics = metricsRef.current;
    const hitRate = metrics.cacheHits + metrics.cacheMisses > 0
      ? ((metrics.cacheHits / (metrics.cacheHits + metrics.cacheMisses)) * 100).toFixed(1)
      : 0;
    
    return {
      ...metrics,
      hitRate: `${hitRate}%`
    };
  }, []);

  // Log metrics periodically
  React.useEffect(() => {
    const interval = setInterval(() => {
      const metrics = getCacheMetrics();
      if (metrics.cacheHits > 0 || metrics.cacheMisses > 0) {
        if (isDebugEnabled) console.log('[MediaCache] 📊 Metrics:', metrics);
      }
    }, 60000); // Log every 60 seconds
    
    return () => clearInterval(interval);
  }, [getCacheMetrics]);

  // Mark video creation time for an album
  const markVideoCreationTime = useCallback((albumId) => {
    const now = Date.now();
    console.log(`⏰ Marking video creation time for album ${albumId}: ${now}`);
    setCache(prev => ({
      ...prev,
      lastVideoCreationTimes: {
        ...prev.lastVideoCreationTimes,
        [albumId]: now
      }
    }));
  }, []);

  // Check if caching should be allowed for an album
  const shouldAllowCaching = useCallback((albumId) => {
    const lastCreationTime = cache.lastVideoCreationTimes?.[albumId];
    if (!lastCreationTime) return true; // No recent video creation
    
    const timeSince = Date.now() - lastCreationTime;
    const allow = timeSince > 15000; // 15 seconds
    console.log(`🕐 shouldAllowCaching(${albumId}): timeSince=${timeSince}ms, allow=${allow}`);
    return allow;
  }, [cache.lastVideoCreationTimes]);

  const value = {
    // Getters
    getCachedPictures,
    getCachedVideos,
    getCachedAlbum,
    getCachedArchivedAssets,
    
    // Setters
    setCachedPictures,
    setCachedVideos,
    setCachedAlbum,
    setCachedArchivedAssets,
    
    // Invalidation
    updatePictureInCache,
    updateVideoInCache,
    addAssetToCache,
    removeAssetFromCache,
    archiveAssetInCache,
    restoreAssetInCache,
    invalidateCache,
    
    // Video creation tracking
    markVideoCreationTime,
    shouldAllowCaching,
    
    // Metrics
    getCacheMetrics
  };
  
  if (isDebugEnabled) console.log('[MediaCache] 🧩 value object created:', {
    getCachedPictures: typeof getCachedPictures,
    setCachedPictures: typeof setCachedPictures,
    getCachedVideos: typeof getCachedVideos,
    setCachedVideos: typeof setCachedVideos,
    getCachedAlbum: typeof getCachedAlbum
  });

  return (
    <MediaCacheContext.Provider value={value}>
      {children}
    </MediaCacheContext.Provider>
  );
};
